import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SignIn } from '@clerk/clerk-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const StudioPublicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const { data } = await api.get(`/widget/${slug}/courses`);
        setStudio(data.studio);
      } catch (err) {
        setError('Studio not found.');
      } finally {
        setLoading(false);
      }
    };
    fetchStudio();
  }, [slug]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studio?.name) {
      document.title = studio.name;
    }
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  // If already logged in, redirect to appropriate dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'student' && user.studioId) {
        navigate('/student');
      } else if (user.role === 'teacher') {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  if (error || !studio) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Studio Not Found</h1>
          <p className="text-gray-500">The studio you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Studio Header */}
        <div className="text-center mb-6">
          {studio.branding?.logo ? (
            <img
              src={studio.branding.logo}
              alt={studio.name}
              className="h-20 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-brand-600">
                {studio.name?.charAt(0) || 'S'}
              </span>
            </div>
          )}
          <h1 className="text-xl font-bold text-gray-900">{studio.name}</h1>
          {studio.location?.city && (
            <p className="text-sm text-gray-500 mt-1">
              {[studio.location.city, studio.location.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>

        {/* Clerk Sign In — embedded directly */}
        <SignIn
          appearance={{
            variables: { colorPrimary: '#3F51B5' },
            elements: {
              rootBox: 'w-full',
              card: 'shadow-lg border border-gray-100 rounded-2xl',
              headerTitle: { display: 'none' },
              headerSubtitle: { display: 'none' }
            }
          }}
        />

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <a href="https://mydancehall.com" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-500">DanceHall</a>
        </p>
      </div>
    </div>
  );
};

export default StudioPublicPage;
